import { GeckoColorScale } from 'color-scale-kit'
import {
  GeckoSummaryStatisticsHistogram,
  useQuantitativeDataSummaryStats,
} from 'features/dataLayers'
import { useRolodexEdit } from 'features/fde'
import { useRolodexCache } from 'features/rolodex'
import React from 'react'
import styled from 'styled-components'
import {
  GeckoColorScaleThreshold,
  GeckoComponentDataFrame,
  GeckoComponentDefaults,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
  RolodexEntryType,
} from 'types'
import { v4 as uuidv4 } from 'uuid'

import { RangeInputs } from './RangeInputs'
import { RangeSlider } from './RangeSlider'

const SaveResetButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 40px;

  & > span {
    height: 1.375rem;
  }
`

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 0.5rem;
`

const HeaderWithIcon = styled.span`
  display: flex;
  cursor: pointer;
  align-items: center;
  font-family: Manrope;
  color: ${({ theme }) => theme.forecast.icon2};
  line-height: 20px;
  text-align: left;
  border-radius: 4px;
  justify-content: center;
  padding: 4px;
  background-color: ${({ theme }) => theme.forecast.background3};
`
interface RangeSelectorProps {
  selectedCDF: GeckoComponentDataFrame
  selectedColorScale: GeckoColorScale
  filteredValueRange: GeckoMeasurementRange | undefined
  onFilteredValueRangeChange: (range: GeckoMeasurementRange | undefined) => void
  selectedComponentDefaults: GeckoComponentDefaults | undefined
  forceRefreshComponentDefaultsFetch: () => void
  selectedColorThreshold: GeckoColorScaleThreshold
}
export const RangeSelector: React.FC<RangeSelectorProps> = ({
  selectedCDF,
  selectedColorScale,
  filteredValueRange,
  onFilteredValueRangeChange,
  selectedComponentDefaults,
  forceRefreshComponentDefaultsFetch,
  selectedColorThreshold,
}) => {
  //----------------------------------------------------------------------------

  const view = selectedCDF.data.params.view
  const unit = selectedCDF.data.params.measurement_unit
  const cdfStats = useQuantitativeDataSummaryStats(selectedCDF).data

  //----------------------------------------------------------------------------

  // if the color scale uses a red green threshold (which most do) force the color
  // to be red yellow green, otherwise use the color scale
  const { minThresholdColor, midThresholdColor, maxThresholdColor } =
    React.useMemo(() => {
      const colorScale = new GeckoColorScale({
        colorcetId: selectedCDF.data.params.view.colorcet.colorcetId,
        valuesToColorRange: [
          [0, selectedCDF.data.params.view.colorcet.start],
          [0.5, selectedCDF.data.params.view.colorcet.middle],
          [1.0, selectedCDF.data.params.view.colorcet.end],
        ],
      })
      return {
        minThresholdColor: colorScale.getColorForValue(0.0),
        midThresholdColor: colorScale.getColorForValue(0.5),
        maxThresholdColor: colorScale.getColorForValue(1.0),
      }
    }, [selectedCDF])

  //----------------------------------------------------------------------------

  const { putEntry, patchEntry, resetEntries } = useRolodexCache()
  const { rolodexPut } = useRolodexEdit()

  const onColorThresholdChanged = React.useCallback(
    (threshold: GeckoColorScaleThreshold) => {
      const convertedThreshold = threshold.convertTo(unit)
      const min = convertedThreshold.min.num
      const mid = convertedThreshold.mid.num
      const max = convertedThreshold.max.num

      if (Number.isNaN(min + mid + max)) return

      if (selectedComponentDefaults) {
        patchEntry({
          id: selectedComponentDefaults.id,
          type: RolodexEntryType.COMPONENT_DEFAULTS,
          data: {
            color_scale_thresholds: {
              [view.id]: {
                ...(!Number.isFinite(min) ? {} : { min }),
                ...(!Number.isFinite(mid) ? {} : { mid }),
                ...(!Number.isFinite(max) ? {} : { max }),
                measurement_unit: unit.id,
              },
            },
          },
          tags: selectedComponentDefaults.tags,
        })
      } else {
        // If id is longer than 75 characters, we need to truncate it
        let id = `${selectedCDF.tags.component}-defaults-${uuidv4()}`
        if (id.length > 75) id = id.slice(0, 75)

        putEntry({
          id,
          type: RolodexEntryType.COMPONENT_DEFAULTS,
          data: {
            color_scale_thresholds: {
              [view.id]: { min, mid, max, measurement_unit: unit.id },
            },
          },
          tags: {
            [RolodexEntryType.ORGANIZATION]: selectedCDF.tags.organization,
            [RolodexEntryType.SITE]: selectedCDF.tags.site,
            [RolodexEntryType.UNIT]: selectedCDF.tags.unit,
            [RolodexEntryType.COMPONENT]: selectedCDF.tags.component,
          },
        })
      }
    },
    [unit, view, selectedCDF, selectedComponentDefaults, patchEntry, putEntry]
  )

  const handleSaveThreshold = React.useCallback(async () => {
    if (!selectedComponentDefaults) return
    await rolodexPut([selectedComponentDefaults.raw])
    await forceRefreshComponentDefaultsFetch()
    resetEntries()
  }, [
    selectedComponentDefaults,
    forceRefreshComponentDefaultsFetch,
    resetEntries,
    rolodexPut,
  ])

  React.useEffect(() => {
    resetEntries()
  }, [view.id, resetEntries])

  //----------------------------------------------------------------------------

  return !cdfStats?.value_range?.size.num || !selectedColorThreshold ? null : (
    <GenericRangeSelector
      cdfId={selectedCDF.id}
      selectedComponentDefaults={selectedComponentDefaults}
      measurementUnit={unit}
      selectedColorScale={selectedColorScale}
      filteredValueRange={filteredValueRange}
      onFilteredValueRangeChange={onFilteredValueRangeChange}
      fullValueRange={cdfStats?.value_range}
      cdfStatsHistogram={cdfStats?.histogram}
      onColorThresholdChanged={onColorThresholdChanged}
      selectedColorThreshold={selectedColorThreshold}
      minThresholdColor={minThresholdColor}
      midThresholdColor={midThresholdColor}
      maxThresholdColor={maxThresholdColor}
      handleSaveThreshold={handleSaveThreshold}
      resetEntries={resetEntries}
    />
  )
}

export const GenericRangeSelector: React.FC<{
  cdfId: string
  selectedComponentDefaults: GeckoComponentDefaults | undefined
  measurementUnit: GeckoMeasurementUnit
  selectedColorScale: GeckoColorScale
  fullValueRange: GeckoMeasurementRange
  filteredValueRange: GeckoMeasurementRange | undefined
  onFilteredValueRangeChange: (range: GeckoMeasurementRange | undefined) => void
  cdfStatsHistogram: GeckoSummaryStatisticsHistogram | undefined
  selectedColorThreshold: GeckoColorScaleThreshold
  minThresholdColor: string
  midThresholdColor: string
  maxThresholdColor: string
  onColorThresholdChanged: (threshold: GeckoColorScaleThreshold) => void
  handleSaveThreshold?: () => void
  resetEntries?: () => void
}> = ({
  cdfId,
  selectedComponentDefaults,
  measurementUnit,
  selectedColorScale,
  fullValueRange,
  filteredValueRange,
  onFilteredValueRangeChange,
  cdfStatsHistogram,
  selectedColorThreshold,
  minThresholdColor,
  midThresholdColor,
  maxThresholdColor,
  onColorThresholdChanged,
  handleSaveThreshold,
  resetEntries,
}) => {
  return (
    <Container data-testid='controlpanel-range-selector'>
      <RangeSlider
        cdfId={cdfId}
        measurementUnit={measurementUnit}
        fullValueRange={fullValueRange}
        filteredValueRange={filteredValueRange}
        onFilteredValueRangeChange={onFilteredValueRangeChange}
        cdfStatsHistogram={cdfStatsHistogram}
        colorThreshold={selectedColorThreshold}
        colorScale={selectedColorScale}
        onColorThresholdChanged={onColorThresholdChanged}
        minThresholdColor={minThresholdColor}
        maxThresholdColor={maxThresholdColor}
        midThresholdColor={midThresholdColor}
      />
      <RangeInputs
        measurementUnit={measurementUnit}
        fullValueRange={fullValueRange}
        filteredValueRange={filteredValueRange}
        onFilteredValueRangeChange={onFilteredValueRangeChange}
        colorThreshold={selectedColorThreshold}
        onColorThresholdChanged={onColorThresholdChanged}
        minThresholdColor={minThresholdColor}
        midThresholdColor={midThresholdColor}
        maxThresholdColor={maxThresholdColor}
      />
      {selectedComponentDefaults?.isFromCache && (
        <SaveResetButtonsContainer>
          <HeaderWithIcon onClick={() => resetEntries?.()}>
            Reset
          </HeaderWithIcon>
          <HeaderWithIcon onClick={() => handleSaveThreshold?.()}>
            Save
          </HeaderWithIcon>
        </SaveResetButtonsContainer>
      )}
    </Container>
  )
}
